import { SwarmType } from '@/graphql/types';
import { useDeepCompareMemoize } from '../../../../hooks/useDeepCompareMemoize';
import type { FieldValues } from '../../../reactForm/types';
import { buildStrictContractYupSchemaFromFormSchema } from '../../../reactForm/validation';
import type { SwarmFormControllerConfig } from '../../measuringPoint/form-controller';
import { SwarmFormController } from '../../measuringPoint/form-controller';
import { FieldsArray } from '../../measuringPoint/fields-array';
import type { TranslatedFormSchema } from '../../measuringPoint/types';
import { LayoutTree } from '../../../reactForm/layout-helper';
import { requireReportElementClass } from '../elements/elementSettingsFormSchema';
import type { ElementSettings, ElementSettingsFormSchema } from '../elements/types';
import { isDefinitionField } from '../typeGuards';
import {
    compileSettingsFormSchemaRuntime,
    type CompiledSettingsFormSchemaRuntime,
} from './settingsFormSchemaRuntime';
import { isRecord } from '../../../../utils/typeGuards';
import { useStableCallback } from './useStableCallback';

interface ControllerConfig extends SwarmFormControllerConfig {
    elementType: string;
    currentSettings: Record<string, unknown>;
    // Seeds the form when it is rebuilt mid-edit, so in-progress values survive a
    // schema change. `currentSettings` stays the saved baseline the save path needs.
    initialFormValues?: Record<string, unknown>;
    settingsFormSchema: ElementSettingsFormSchema;
    onSubmitSettings: (nextSettings: Record<string, unknown>) => void;
    onSubmitError: (error: unknown) => void;
}

interface UseElementSettingsFormControllerArgs {
    elementType: string;
    currentSettings: Record<string, unknown>;
    initialFormValues?: Record<string, unknown>;
    settingsFormSchema: ElementSettingsFormSchema;
    onSubmitSettings: (nextSettings: Record<string, unknown>) => void;
    onSubmitError: (error: unknown) => void;
}

function prepareElementSettingsForSave(
    elementType: string,
    currentSettings: ElementSettings,
    nextSettings: ElementSettings,
    touchedSettingNames: ReadonlySet<string>
): ElementSettings {
    const ElementClass = requireReportElementClass(elementType);
    const normalizedSettings =
        ElementClass.normalizeSettingsForSave?.(
            currentSettings,
            nextSettings,
            touchedSettingNames
        ) ?? nextSettings;

    ElementClass.validateSettings(normalizedSettings);

    return normalizedSettings;
}

export class ReportBuilderElementSettingsFormController extends SwarmFormController {
    declare elementType: string;

    declare currentSettings: Record<string, unknown>;

    declare initialFormValues: Record<string, unknown> | undefined;

    declare settingsFormSchema: ElementSettingsFormSchema;

    declare settingsFormRuntime: CompiledSettingsFormSchemaRuntime;

    declare onSubmitSettings: (nextSettings: Record<string, unknown>) => void;

    declare onSubmitError: (error: unknown) => void;

    constructor(config: ControllerConfig) {
        super(config as SwarmFormControllerConfig);
    }

    storeConfig({
        elementType,
        currentSettings,
        initialFormValues,
        settingsFormSchema,
        onSubmitSettings,
        onSubmitError,
        ...config
    }: ControllerConfig) {
        this.elementType = elementType;
        this.currentSettings = currentSettings;
        this.initialFormValues = initialFormValues;
        this.settingsFormSchema = settingsFormSchema;
        this.settingsFormRuntime = compileSettingsFormSchemaRuntime(settingsFormSchema);
        this.onSubmitSettings = onSubmitSettings;
        this.onSubmitError = onSubmitError;

        super.storeConfig(config);
    }

    createValidationSchema() {
        return buildStrictContractYupSchemaFromFormSchema(this.settingsFormSchema);
    }

    createFields(): TranslatedFormSchema['fields'] {
        const fields = FieldsArray.from(
            this.settingsFormRuntime.fields.map((fieldRuntime) => fieldRuntime.toLayoutField())
        );

        return Object.freeze(fields) as TranslatedFormSchema['fields'];
    }

    createLayoutTree(): LayoutTree {
        return new LayoutTree({
            name: 'root',
            groups: [
                new LayoutTree({
                    name: 'elementSettings',
                    fields: this.settingsFormRuntime.fields
                        .filter((fieldRuntime) => !isDefinitionField(fieldRuntime.field))
                        .map((fieldRuntime) => this.fields.getByName(fieldRuntime.field.name)),
                }),
            ],
        });
    }

    createDefaultValues(): Record<string, unknown> {
        return this.settingsFormRuntime.createDefaultValues(
            this.initialFormValues ?? this.currentSettings
        );
    }

    createSettingsDraft(values: FieldValues, changedSettingName: string): ElementSettings {
        if (!isRecord(values)) {
            throw new Error(`Expected "${this.elementType}" form values to be an object.`);
        }

        const settingsDraft = { ...this.currentSettings };

        this.settingsFormSchema.fields.forEach((field) => {
            const fieldState = this.rhfProps.getFieldState(field.name);
            const shouldPreserveFormValue =
                field.name === changedSettingName ||
                Object.prototype.hasOwnProperty.call(this.currentSettings, field.name) ||
                fieldState.isDirty ||
                fieldState.isTouched;

            if (shouldPreserveFormValue) {
                settingsDraft[field.name] = values[field.name];
            }
        });

        return settingsDraft;
    }

    private getTouchedSettingNames(): ReadonlySet<string> {
        const touchedSettingNames = new Set<string>();

        this.settingsFormSchema.fields.forEach((field) => {
            if (this.rhfProps.getFieldState(field.name).isTouched) {
                touchedSettingNames.add(field.name);
            }
        });

        return touchedSettingNames;
    }

    onSubmitFunction(values: FieldValues): Promise<void> {
        try {
            if (!isRecord(values)) {
                throw new Error(`Expected "${this.elementType}" form values to be an object.`);
            }
            const nextSettings = this.settingsFormRuntime.applyValidatedValues(
                this.currentSettings,
                values
            );
            const normalizedSettings = prepareElementSettingsForSave(
                this.elementType,
                this.currentSettings,
                nextSettings,
                this.getTouchedSettingNames()
            );
            this.onSubmitSettings(normalizedSettings);
        } catch (error) {
            this.onSubmitError(error);
        }

        return Promise.resolve();
    }
}

export function useElementSettingsFormController({
    elementType,
    currentSettings,
    initialFormValues,
    settingsFormSchema,
    onSubmitSettings,
    onSubmitError,
}: UseElementSettingsFormControllerArgs): ReportBuilderElementSettingsFormController {
    const stableCurrentSettings = useDeepCompareMemoize(currentSettings);
    const stableInitialFormValues = useDeepCompareMemoize(initialFormValues);
    const stableSettingsFormSchema = useDeepCompareMemoize(settingsFormSchema);
    const stableOnSubmitSettings = useStableCallback(onSubmitSettings);
    const stableOnSubmitError = useStableCallback(onSubmitError);

    return ReportBuilderElementSettingsFormController.use(SwarmType.Vibration, false, {
        elementType,
        currentSettings: stableCurrentSettings,
        initialFormValues: stableInitialFormValues,
        settingsFormSchema: stableSettingsFormSchema,
        onSubmitSettings: stableOnSubmitSettings,
        onSubmitError: stableOnSubmitError,
    }) as ReportBuilderElementSettingsFormController;
}
